import * as React from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";

import { useMachine } from "@xstate/react";

import { searchFormMachine } from "./SearchForm.state";

import {
  OpportunitySearchQueryState,
  opportunityResultStore,
  useGlobal,
  usePageEnvelope,
  useThunkDispatch,
} from "@skv/state";

import {
  AsyncTypeahead,
  AsyncTypeaheadProps,
  OpportunitySearchResults,
} from "@skv/core";

const SearchForm = () => {
  const history = useHistory();
  const location = useLocation<{
    searchQuery: OpportunitySearchQueryState;
    shouldUpdateSearchForm?: boolean;
  }>();

  const dispatch = useThunkDispatch();

  const { pageEnvelope } = usePageEnvelope<{
    searchCriteria: OpportunitySearchQueryState;
  }>();

  const { categoryList } = useGlobal();

  const match = useRouteMatch({
    path: ["/*-volunteering", "/volunteering*"],
  });

  const initialSearchQuery = React.useMemo(() => {
    return Object.assign(
      {},
      pageEnvelope.data?.searchCriteria,
      location.state?.searchQuery
    );
  }, []);

  const [current, send] = useMachine(searchFormMachine, {
    context: {
      keyword: initialSearchQuery.keyword || "",
      where: initialSearchQuery.where || "",
      category: initialSearchQuery.category || [],
    },
  });

  const categoryOptions = React.useMemo(() => {
    return categoryList.filter(
      (n) =>
        n.name
          .toLocaleLowerCase()
          .indexOf(current.context.keyword.toLocaleLowerCase()) > -1
    );
  }, [categoryList, current.context.keyword]);

  const defaultKeywordSelected = React.useMemo(() => {
    if (current.context.keyword) {
      return [];
    }

    return current.context.category
      .map((id) => categoryList.find((l) => l.id === id))
      .filter((n) => n != null);
  }, []);

  const [isMenuOpen, setMenuOpen] = React.useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();

    let query: OpportunitySearchQueryState = {};

    query = {
      ...location.state?.searchQuery,
      keyword: current.context.category.length ? "" : current.context.keyword,
      category: [
        ...(location.state?.searchQuery?.category
          ? location.state.searchQuery.category
          : []),
        ...current.context.category,
      ],
      where: current.context.where,
    };

    if ("page" in query) {
      delete query.page;
    }

    const createNextHistoryState = (data: OpportunitySearchResults) => ({
      searchQuery: {
        ...query,
        where: data.canonicalLocation || query.where,
      },
      analytics: {
        didKeywordAutoComplete: current.context.didKeywordAutoComplete,
        didWhereAutoComplete: current.context.didWhereAutoComplete,
      },
      shouldUpdateSearchForm: true,
    });

    /*
      push to /volunteering url to show search page
      if current url is not on the search page.
      This is needed to show the loading states,
      otherwise on a slow connection for example,
      it would just stay on the home page and only change
      page when the request is finished, and the user will
      not see the loading indicator.
    */
    if (!match) {
      history.push(`/volunteering`, { searchQuery: query });
      dispatch(opportunityResultStore.actions.fetchOpportunities(query)).then(
        (data) => {
          history.replace(data.canonicalUrl, createNextHistoryState(data));
        }
      );
    } else {
      dispatch(opportunityResultStore.actions.fetchOpportunities(query)).then(
        (data) => {
          history.push(data.canonicalUrl, createNextHistoryState(data));
        }
      );
    }
  };

  const renderMenu = React.useCallback<
    AsyncTypeaheadProps<string>["renderMenu"]
  >(
    (results, menuProps, { Menu, MenuItem, MenuHeader }) => {
      // console.log(results);
      if (
        current.matches("initialSuggestions.loading") ||
        current.matches("relatedSuggestions.loading")
      ) {
        return (
          <Menu
            {...menuProps}
            ref={menuProps.ref as React.MutableRefObject<HTMLUListElement>}
            aria-labelledby="where-heading"
          >
            <MenuHeader id="where-heading">
              <p className="mt-3">Loading please wait...</p>
            </MenuHeader>
          </Menu>
        );
      }

      // render initial suggestions if where input is < 2 chars
      if (current.context.where.length <= 2) {
        return (
          <Menu
            {...menuProps}
            ref={menuProps.ref as React.MutableRefObject<HTMLUListElement>}
            aria-labelledby="where-heading"
          >
            <MenuHeader id="remote-heading">Remote</MenuHeader>
            <MenuItem option={results[0]} position={0} />
            <MenuHeader id="where-heading">Suggested areas</MenuHeader>
            {results.map((result, idx) => {
              if (idx === 0) {
                return null;
              }
              return <MenuItem key={idx} option={result} position={idx} />;
            })}
          </Menu>
        );
      }

      return (
        <Menu
          {...menuProps}
          ref={menuProps.ref as React.MutableRefObject<HTMLUListElement>}
          aria-labelledby="where-heading"
        >
          {results.length ? (
            results.map((result, idx) => (
              <MenuItem key={idx} option={result} position={idx} />
            ))
          ) : (
            <MenuHeader id="where-heading">No results</MenuHeader>
          )}
        </Menu>
      );
    },
    [current]
  );

  React.useEffect(() => {
    if (
      !location.state ||
      typeof location.state.shouldUpdateSearchForm === "undefined"
    ) {
      return;
    }

    if (location.state.shouldUpdateSearchForm === true) {
      send({
        type: "KEYWORD_INPUT_CHANGE",
        value: location.state?.searchQuery?.keyword || "",
      });

      send({
        type: "LOCATION_INPUT_CHANGE",
        value: location.state?.searchQuery?.where || "",
      });
    }
  }, [location.state, send]);

  return (
    <section aria-label="Search">
      <div className="nsw-container mt-n5">
        <div className="bg-secondary p-4">
          <form
            css={`
              .form-control {
                border-color: #fff;
              }
            `}
            method="GET"
            action="/volunteering"
            onSubmit={handleSubmit}
          >
            <div className="row align-items-end mx-n2 mt-lg-n3">
              <div
                className="col-md nsw-form__group d-flex flex-column px-2"
                css={`
                  overflow-x: clip;
                `}
              >
                <label className="nsw-form__label" htmlFor="keyword">
                  What
                </label>
                <small className="mb-2">What are you interested in?</small>
                <AsyncTypeahead<typeof categoryOptions[0]>
                  id="keyword"
                  inputProps={{
                    name: "keyword",
                    autoComplete: "off",
                    className: "nsw-form__input focus-primary border-primary",
                    value: current.context.keyword || "",
                  }}
                  wrapperProps={{
                    className: "nsw-form__label",
                  }}
                  labelKey={(option) => {
                    return option.name;
                  }}
                  onInputChange={(text) => {
                    send({ type: "KEYWORD_INPUT_CHANGE", value: text });
                  }}
                  onSearch={() => {
                    return;
                  }}
                  onChange={(selected) => {
                    if (selected.length) {
                      send({
                        type: "KEYWORD_INPUT_SELECT",
                        value: selected[0],
                      });
                      setMenuOpen(false);
                    }
                  }}
                  onFocus={() => {
                    setMenuOpen(true);
                  }}
                  onBlur={() => {
                    setMenuOpen(false);
                  }}
                  onKeyDown={(e) => {
                    switch (e.key) {
                      case "ArrowUp":
                      case "ArrowDown":
                        if (!isMenuOpen) {
                          setMenuOpen(true);
                          return false;
                        }
                        break;
                      default:
                        break;
                    }
                  }}
                  // isLoading={current.matches("relatedSuggestions.loading")}
                  open={isMenuOpen && categoryOptions.length !== 0}
                  isLoading={false}
                  options={categoryOptions}
                  selected={defaultKeywordSelected}
                  minLength={0}
                  renderMenu={(
                    results,
                    menuProps,
                    { Menu, MenuItem, MenuHeader }
                  ): JSX.Element => {
                    return (
                      <Menu
                        {...menuProps}
                        ref={
                          menuProps.ref as React.MutableRefObject<HTMLUListElement>
                        }
                        aria-labelledby="category-heading"
                      >
                        <MenuHeader id="category-heading">
                          Categories
                        </MenuHeader>
                        {results.map((n, idx) => (
                          <MenuItem key={idx} option={n} position={idx} />
                        ))}
                      </Menu>
                    );
                  }}
                />
              </div>
              <div
                className="col-md nsw-form__group d-flex flex-column px-2 mb-3 mb-md-0"
                css={`
                  overflow-x: clip;
                `}
              >
                <label className="nsw-form__label" htmlFor="where">
                  Location
                </label>
                <small className="mb-2">
                  Enter suburb, town, city or postcode
                </small>
                <AsyncTypeahead<string>
                  id="where"
                  inputProps={{
                    id: "where",
                    name: "where",
                    autoComplete: "off",
                    className: "nsw-form__input focus-primary border-primary",
                    value: current.context.where || "",
                  }}
                  wrapperProps={{
                    className: "nsw-form__label",
                  }}
                  onInputChange={(text) =>
                    send({ type: "LOCATION_INPUT_CHANGE", value: text })
                  }
                  onSearch={(text) =>
                    send({ type: "FETCH_START_LOCATIONS", value: text })
                  }
                  onChange={(selected) => {
                    console.log(selected);
                    if (selected.length) {
                      send({
                        type: "LOCATION_INPUT_SELECT",
                        value: selected[0],
                      });
                    }
                  }}
                  isLoading={current.matches("relatedSuggestions.loading")}
                  options={(() => {
                    if (current.context.where.length <= 2) {
                      console.log("CHANGING TO INITIAL SUGGESTIONS");
                      return current.context.initialSuggestions;
                    }

                    console.log("CHANGING TO RELATED SUGGESTIONS");

                    return current.context.relatedSuggestions || [];
                  })()}
                  selected={
                    current.context.where ? [current.context.where] : []
                  }
                  minLength={0}
                  renderMenu={renderMenu}
                />
              </div>
              <div className="px-2 nsw-form__group flex-grow-1 flex-md-grow-0">
                <button
                  aria-label="Find opportunities"
                  className="btn btn-block btn-primary"
                  css={`
                    min-width: 100px;
                  `}
                >
                  Find
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default SearchForm;
